"use client"

import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react"

export interface Notification {
  id: string
  source: "linear" | "notion" | "slack" | "google" | "sophy"
  title: string
  message: string
  timestamp: string
  isRead: boolean
  type: "info" | "success" | "warning" | "error"
}

interface NotificationsContextType {
  notifications: Notification[]
  unreadCount: number
  addNotification: (notification: Omit<Notification, "id">) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  deleteNotification: (id: string) => void
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined)

const NOTIFICATIONS_STORAGE_KEY = "sophy-notifications"

function loadNotifications(): Notification[] {
  if (typeof window === "undefined") return []
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveNotifications(notifications: Notification[]) {
  localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(notifications))
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>(() => loadNotifications())

  useEffect(() => {
    saveNotifications(notifications)
  }, [notifications])

  const unreadCount = notifications.filter((n) => !n.isRead).length

  const addNotification = useCallback((notification: Omit<Notification, "id">) => {
    setNotifications((prev) => {
      const isDuplicate = prev.some(
        (n) => n.source === notification.source && n.title === notification.title && n.message === notification.message
      )
      if (isDuplicate) return prev

      const id = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      const next = [{ ...notification, id }, ...prev]
      saveNotifications(next)
      return next
    })
  }, [])

  const markAsRead = (id: string) => {
    setNotifications(notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)))
  }

  const markAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, isRead: true })))
  }

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter((n) => n.id !== id))
  }

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationsProvider")
  }
  return context
}
